import {ABTestNames} from '../constants'

export type ABTestConfig = Record<string, number>

const isVariant = (value: unknown): value is number => value === 0 || value === 1

// The stored cookie, left unvalidated here: values are checked per experiment as they are read
const parseStoredConfig = (cookieValue: string | undefined): Record<string, unknown> => {
  try {
    const parsed: unknown = JSON.parse(cookieValue || '{}')
    // A malformed cookie should not take the page down — re-roll instead.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {}
    }
    return parsed as Record<string, unknown>
  } catch {
    return {}
  }
}

/**
 * Turns the stored cookie value and the current query string into the variant assignment for
 * every known experiment.
 *
 * Pure, and called only from an effect in the browser (`ABTestProvider`): assigning a variant
 * involves `Math.random()`, which must not run while rendering a page that gets prerendered - the
 * roll would be baked into the HTML and every visitor would get the same "random" variant.
 */
export const resolveABTestConfig = (
  cookieValue: string | undefined,
  searchParams: URLSearchParams | null,
): ABTestConfig => {
  const stored = parseStoredConfig(cookieValue)
  const config: ABTestConfig = {}

  // Built from the known experiments rather than from the cookie, so an experiment retired since
  // the cookie was written is dropped instead of being carried around forever.
  for (const name of Object.values(ABTestNames)) {
    // Overrides to AB test in the url, for example to set specific version for cypress. They are
    // a testing tool, and are persisted like any other assignment so the chosen variant survives
    // the rest of the visit.
    const override = searchParams?.get(name)
    if (override === '0' || override === '1') {
      config[name] = parseInt(override, 10)
      continue
    }

    const storedVariant = stored[name]
    config[name] = isVariant(storedVariant) ? storedVariant : Math.floor(Math.random() * 2)
  }

  return config
}
